import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Heart, MessageCircle, Send } from 'lucide-react';
import { CheckIn } from '../types';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { likeCheckIn, unlikeCheckIn, addComment } from '../services/checkInService';

interface CheckInFeedProps {
  checkIns: CheckIn[];
  onCheckInUpdate: () => void;
}

const CheckInFeed: React.FC<CheckInFeedProps> = ({ checkIns, onCheckInUpdate }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activeComment, setActiveComment] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');

  const formatDate = (date: Date) => {
    try {
      return format(date, "d 'de' MMM 'de' yyyy HH:mm", { locale: ptBR });
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('eventDetail.invalidDate');
    }
  };

  const handleLike = async (checkInId: string) => {
    if (!user) return;

    try {
      const checkIn = checkIns.find(c => c.id === checkInId);
      if (!checkIn) return;

      const hasLiked = checkIn.likes?.includes(user.id);
      
      if (hasLiked) {
        await unlikeCheckIn(checkInId, user.id);
      } else {
        await likeCheckIn(checkInId, user.id);
      }
      
      onCheckInUpdate();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleComment = async (checkInId: string) => {
    if (!user || !commentText.trim()) return;

    try {
      await addComment(
        checkInId,
        user.id,
        user.name,
        commentText.trim(),
        user.photoURL
      );
      
      setCommentText('');
      setActiveComment(null);
      onCheckInUpdate();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const getInitial = (name: string | undefined) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };

  return (
    <div className="space-y-6">
      {checkIns.map((checkIn) => (
        <div key={checkIn.id} className="bg-white rounded-lg shadow p-4">
          <div className="flex items-start space-x-4">
            {checkIn.userPhoto ? (
              <img
                src={checkIn.userPhoto}
                alt={checkIn.userName || t('eventDetail.anonymousUser')}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-600 font-medium text-lg">
                  {getInitial(checkIn.userName)}
                </span>
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">{checkIn.userName || t('eventDetail.anonymousUser')}</h4>
                <span className="text-sm text-gray-500">
                  {formatDate(checkIn.createdAt)}
                </span>
              </div>
              
              {checkIn.message && (
                <p className="mt-1 text-gray-600">{checkIn.message}</p>
              )}

              <div className="mt-3 flex items-center space-x-4">
                <button
                  onClick={() => handleLike(checkIn.id)}
                  className={`flex items-center space-x-1 text-sm ${
                    checkIn.likes?.includes(user?.id || '') 
                      ? 'text-red-500' 
                      : 'text-gray-500 hover:text-red-500'
                  }`}
                >
                  <Heart className="h-4 w-4" />
                  <span>{checkIn.likes?.length || 0}</span>
                </button>
                
                <button
                  onClick={() => setActiveComment(activeComment === checkIn.id ? null : checkIn.id)}
                  className="flex items-center space-x-1 text-sm text-gray-500 hover:text-indigo-500"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>{checkIn.comments?.length || 0}</span>
                </button>
              </div>

              {checkIn.comments && checkIn.comments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {checkIn.comments.map((comment) => (
                    <div key={comment.id} className="flex items-start space-x-2">
                      {comment.userPhoto ? (
                        <img
                          src={comment.userPhoto}
                          alt={comment.userName || t('eventDetail.anonymousUser')}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-600 text-sm">
                            {getInitial(comment.userName)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 bg-gray-50 rounded-lg p-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{comment.userName || t('eventDetail.anonymousUser')}</span>
                          <span className="text-xs text-gray-500">
                            {formatDate(comment.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeComment === checkIn.id && (
                <div className="mt-3 flex items-center space-x-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t('eventDetail.checkIn.writeComment')}
                    className="flex-1 border border-gray-300 rounded-full px-4 py-1 text-sm focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    onClick={() => handleComment(checkIn.id)}
                    disabled={!commentText.trim()}
                    className="text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CheckInFeed;